import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Modal, inputCls, labelCls, btnPrimary, fmtDate } from "@/components/kit";
import { FolderSimple, UploadSimple, FileText, FilePdf, FileImage, DownloadSimple, Trash, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const iconFor = (ct = "") => ct.includes("pdf") ? FilePdf : ct.startsWith("image/") ? FileImage : FileText;
const sizeFmt = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

export default function Files() {
  const { user } = useAuth();
  const canUpload = ["athletic_director", "coach"].includes(user?.role);
  const [files, setFiles] = useState(null);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [audience, setAudience] = useState("school");
  const [desc, setDesc] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    const reqs = [api.get("/files")];
    if (canUpload) reqs.push(api.get("/teams"));
    const [f, t] = await Promise.all(reqs);
    setFiles(f.data);
    if (t) setTeams(t.data);
  };
  useEffect(() => { load(); }, []);

  const doUpload = async () => {
    if (!picked) { toast.error("Choose a file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", picked);
      fd.append("audience", audience);
      fd.append("description", desc);
      await api.post("/files", fd);
      toast.success("File uploaded");
      setOpen(false); setPicked(null); setDesc(""); setAudience("school");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };

  const download = async (f) => {
    setBusy(f.id);
    try {
      const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: f.content_type }));
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download"); } finally { setBusy(null); }
  };

  const del = async (id) => { if (!window.confirm("Delete this file?")) return; await api.delete(`/files/${id}`); toast.success("Deleted"); load(); };

  if (!files) return <Loading />;

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-5xl mx-auto">
      <PageHeader eyebrow="Shared Documents" title="Files" icon={FolderSimple}>
        {canUpload && <button onClick={() => setOpen(true)} className={btnPrimary} data-testid="upload-file-button"><UploadSimple size={18} weight="bold" /> Upload File</button>}
      </PageHeader>

      {files.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="empty-files">
          <FolderSimple size={40} weight="duotone" color="#737373" className="mx-auto mb-4" />
          <p className="text-[#A3A3A3]">{canUpload ? "No files yet. Upload a document to share." : "No files have been shared with you yet."}</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="file-list">
          {files.map((f) => {
            const Icon = iconFor(f.content_type);
            const canDelete = user.role === "athletic_director" || f.uploader_name === user.name;
            return (
              <Card key={f.id} className="p-4 flex items-center gap-4" data-testid={`file-${f.id}`}>
                <div className="w-11 h-11 rounded-sm bg-[#FF3B30]/10 flex items-center justify-center shrink-0"><Icon size={22} weight="fill" color="#FF3B30" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white truncate">{f.original_filename}</div>
                  <div className="text-xs text-[#737373] truncate">
                    <span className="text-[#FF3B30] font-semibold uppercase tracking-wider">{f.audience_name}</span>
                    {f.description ? ` · ${f.description}` : ""} · {sizeFmt(f.size)} · {f.uploader_name} · {fmtDate(f.created_at)}
                  </div>
                </div>
                <button onClick={() => download(f)} className="text-[#A3A3A3] hover:text-white p-2" data-testid={`download-${f.id}`}>
                  {busy === f.id ? <Spinner size={18} className="animate-spin" /> : <DownloadSimple size={20} weight="bold" />}
                </button>
                {canDelete && <button onClick={() => del(f.id)} className="text-[#737373] hover:text-[#FF3B30] p-2" data-testid={`delete-file-${f.id}`}><Trash size={18} weight="bold" /></button>}
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Upload File" testid="upload-modal">
        <div className="space-y-4">
          <div onClick={() => fileRef.current?.click()} className="border border-dashed border-white/20 hover:border-[#FF3B30]/50 rounded-sm p-8 text-center cursor-pointer transition-colors" data-testid="file-dropzone">
            <input ref={fileRef} type="file" hidden onChange={(e) => setPicked(e.target.files?.[0] || null)} data-testid="file-input" />
            <UploadSimple size={28} weight="bold" color="#FF3B30" className="mx-auto mb-2" />
            <div className="text-sm text-white font-semibold">{picked ? picked.name : "Tap to choose a file"}</div>
            <div className="text-xs text-[#737373] mt-1">{picked ? sizeFmt(picked.size) : "PDF, image or document · max 25MB"}</div>
          </div>
          <div>
            <label className={labelCls}>Who can view this?</label>
            <select className={inputCls} value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="file-audience-select">
              <option value="school">Everyone (school-wide)</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name} (players + parents)</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Description (optional)</label>
            <input className={inputCls} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. Week 1 playbook" data-testid="file-desc-input" />
          </div>
          <button onClick={doUpload} disabled={uploading} className={`${btnPrimary} w-full`} data-testid="confirm-upload-button">
            {uploading ? <><Spinner size={18} className="animate-spin" /> Uploading…</> : <>Upload</>}
          </button>
        </div>
      </Modal>
    </div>
  );
}
